import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import { Badge } from "@material-ui/core";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/QuestionAnswerOutlined";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";
import useTickets from "../hooks/useTickets";
import openSocket from "../services/socket-io";
import { TICKETS_REFRESH_EVENT } from "../utils/ticketRefresh";
import { groupInternalTickets } from "../utils/internalConversations";

function ListItemLink(props) {
  const { icon, primary, to, className, indicators } = props;

  const renderLink = React.useMemo(
    () =>
      React.forwardRef(function ListItemLinkComponent(itemProps, ref) {
        return (
        <RouterLink to={to} ref={ref} {...itemProps} />
        );
      }),
    [to]
  );
  renderLink.displayName = "ListItemLink";

  return (
    <li>
      <ListItem button component={renderLink} className={className}>
        {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
        <ListItemText primary={primary} />
        {indicators}
      </ListItem>
    </li>
  );
}

const MainListItems = (props) => {
  const { drawerClose } = props;
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [ticketsRefreshKey, setTicketsRefreshKey] = useState(0);
  const userQueueIds = user?.queues?.map(queue => queue.id) || [];
  const { tickets: pendingTickets } = useTickets({
    status: "pending",
    queueIds: JSON.stringify(userQueueIds),
    refreshKey: ticketsRefreshKey,
  });
  const { tickets: openTickets } = useTickets({
    status: "open",
    showAll: user?.profile?.toUpperCase() === "ADMIN" ? "true" : undefined,
    queueIds: JSON.stringify(userQueueIds),
    refreshKey: ticketsRefreshKey,
  });
  const pendingCount = groupInternalTickets(pendingTickets, whatsApps).length;
  const unreadOpenMessages = groupInternalTickets(openTickets, whatsApps).reduce(
    (total, ticket) => total + (ticket.unreadMessages || 0),
    0
  );

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  useEffect(() => {
    const socket = openSocket();
    let refreshTimeout;

    const schedulePendingRefresh = () => {
      clearTimeout(refreshTimeout);
      refreshTimeout = setTimeout(() => {
        setTicketsRefreshKey(previous => previous + 1);
      }, 250);
    };

    socket.on("connect", () => socket.emit("joinNotification"));
    socket.on("ticket", schedulePendingRefresh);
    socket.on("appMessage", schedulePendingRefresh);
    window.addEventListener(TICKETS_REFRESH_EVENT, schedulePendingRefresh);

    return () => {
      clearTimeout(refreshTimeout);
      socket.off("ticket", schedulePendingRefresh);
      socket.off("appMessage", schedulePendingRefresh);
      window.removeEventListener(TICKETS_REFRESH_EVENT, schedulePendingRefresh);
      socket.disconnect();
    };
  }, []);

  return (
    <div onClick={drawerClose}>
      <ListItemLink
        to="/"
        primary="Dashboard"
        icon={<DashboardOutlinedIcon />}
      />
      <ListItemLink
        to="/connections"
        primary={i18n.t("mainDrawer.listItems.connections")}
        icon={
          <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
            <SyncAltIcon />
          </Badge>
        }
      />
      <ListItemLink
        to="/tickets"
        primary={i18n.t("mainDrawer.listItems.tickets")}
        icon={<WhatsAppIcon />}
        indicators={
          <div
            title={`${unreadOpenMessages} mensajes nuevos · ${pendingCount} tickets en cola`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minWidth: 46,
              marginRight: 8,
            }}
          >
            <Badge
              badgeContent={unreadOpenMessages}
              color="primary"
              max={99}
            >
              <span />
            </Badge>
            <Badge badgeContent={pendingCount} color="secondary" max={99}>
              <span />
            </Badge>
          </div>
        }
      />

      <ListItemLink
        to="/contacts"
        primary={i18n.t("mainDrawer.listItems.contacts")}
        icon={<ContactPhoneOutlinedIcon />}
      />
      <ListItemLink
        to="/quickAnswers"
        primary={i18n.t("mainDrawer.listItems.quickAnswers")}
        icon={<QuestionAnswerOutlinedIcon />}
      />
      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            <Divider />
            <ListSubheader inset>
              {i18n.t("mainDrawer.listItems.administration")}
            </ListSubheader>
            <ListItemLink
              to="/users"
              primary={i18n.t("mainDrawer.listItems.users")}
              icon={<PeopleAltOutlinedIcon />}
            />
            <ListItemLink
              to="/queues"
              primary={i18n.t("mainDrawer.listItems.queues")}
              icon={<AccountTreeOutlinedIcon />}
            />
            <ListItemLink
              to="/settings"
              primary={i18n.t("mainDrawer.listItems.settings")}
              icon={<SettingsOutlinedIcon />}
            />
          </>
        )}
      />
    </div>
  );
};

export default MainListItems;
